import React, { useEffect, useMemo, useState } from 'react';
import { getAllOrdersAsAdmin } from '../../api/orderApi';
import { useAuth } from '../../context/AuthContext';

const AdminOrdersPage = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    status: '',
    date: '',
    search: '',
  });

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        if (!user?.isAdmin) {
          setError('Not authorized');
          return;
        }

        const data = await getAllOrdersAsAdmin();
        setOrders(data);
      } catch (err) {
        setError(err.message || 'Failed to fetch orders');
      }
    };

    fetchOrders();
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // 🔎 GLOBAL SEARCH + FILTERING (ANY COLUMN)
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const search = filters.search.toLowerCase();

      const orderId = order._id?.toLowerCase() || '';
      const name = order.userId?.name?.toLowerCase() || '';
      const email = order.userId?.email?.toLowerCase() || '';
      const status = order.status?.toLowerCase() || '';
      const date = new Date(order.createdAt).toISOString().split('T')[0];

      const matchesSearch =
        !search ||
        orderId.includes(search) ||
        name.includes(search) ||
        email.includes(search) ||
        status.includes(search);

      const matchesStatus = filters.status ? order.status === filters.status : true;

      const matchesDate = filters.date ? date === filters.date : true;

      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [orders, filters]);

  if (error) {
    return (
      <div className="p-6 text-red-500 font-semibold">
        {error}
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <h2 className="text-2xl font-bold text-gray-800">
          🧾 Orders Dashboard
        </h2>

        <input
          type="text"
          name="search"
          placeholder="Search by order, user, email, status..."
          value={filters.search}
          onChange={handleChange}
          className="w-full md:w-96 px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>

      {/* FILTER BAR */}
      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <select
          name="status"
          value={filters.status}
          onChange={handleChange}
          className="px-3 py-2 border rounded-lg"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
          <option value="completed">Completed</option>
        </select>

        <input
          type="date"
          name="date"
          value={filters.date}
          onChange={handleChange}
          className="px-3 py-2 border rounded-lg"
        />
      </div>

      {/* TABLE */}
      <div className="bg-white shadow-lg rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left">Order ID</th>
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Status</th>
              </tr>
            </thead>

            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-6 text-gray-500">
                    No orders found.
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => {
                  const status = order.status ?? 'missing';

                  const statusStyle = {
                    completed: 'bg-purple-100 text-purple-700',
                    delivered: 'bg-green-100 text-green-700',
                    shipped: 'bg-blue-100 text-blue-700',
                    pending: 'bg-yellow-100 text-yellow-700',
                  }[status] || 'bg-red-100 text-red-700';

                  return (
                    <tr
                      key={order._id}
                      className="border-t hover:bg-gray-50 transition"
                    >
                      <td className="px-4 py-3 font-mono text-xs">
                        {order._id}
                      </td>

                      <td className="px-4 py-3">
                        {order.userId?.name || 'Unknown'}
                      </td>

                      <td className="px-4 py-3 text-gray-600">
                        {order.userId?.email || 'No email'}
                      </td>

                      <td className="px-4 py-3">
                        {new Date(order.createdAt).toLocaleString()}
                      </td>

                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyle}`}>
                          {status}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminOrdersPage;